import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Truck, Upload, X } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const COURIERS = ["Delhivery", "Blue Dart", "DTDC", "India Post", "Shiprocket", "Ekart", "Xpressbees", "Other"];

/**
 * For sellers to upload shipping proof.
 * Props:
 *   order — the order object (must have items[].seller_id === current user)
 *   onSaved: () => void
 */
export default function ShippingProofForm({ order, onSaved }) {
  const [awb, setAwb] = useState("");
  const [courier, setCourier] = useState("Delhivery");
  const [photo, setPhoto] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);
  const [deadline, setDeadline] = useState(null);

  useEffect(() => {
    api.get(`/orders/${order.order_id}/shipping-proof`)
      .then(({ data }) => {
        setExisting(data.proofs?.[0] || null);
        setDeadline(data.deadline);
      })
      .catch(() => {});
  }, [order.order_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!awb.trim()) return toast.error("Enter AWB / tracking number");
    if (!photo) return toast.error("Upload package photo");
    setSaving(true);
    try {
      await api.post(`/orders/${order.order_id}/shipping-proof`, {
        awb, courier, package_photo_url: photo, notes: notes || null,
      });
      toast.success("Proof uploaded · buyer can now track");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const overdue = deadline && new Date(deadline) < new Date();

  if (existing) {
    return (
      <div className="bg-[#D4FF00] border-2 border-black brutal-shadow-sm p-4" data-testid={`ship-proof-uploaded-${order.order_id}`}>
        <div className="flex items-center gap-2">
          <Truck className="w-4 h-4" />
          <p className="font-display text-lg">SHIPPED · {existing.courier}</p>
        </div>
        <p className="font-mono-b text-xs mt-1"><b>AWB:</b> {existing.awb}</p>
        {existing.package_photo_url && (
          <a href={existing.package_photo_url} target="_blank" rel="noreferrer" className="block mt-2 border-2 border-black w-32">
            <img src={existing.package_photo_url} alt="package" className="w-full aspect-square object-cover" />
          </a>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border-2 border-black brutal-shadow-sm p-4 space-y-3" data-testid={`ship-proof-form-${order.order_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Truck className="w-4 h-4" />
        <h4 className="font-display text-lg">UPLOAD SHIPPING PROOF</h4>
        {deadline && (
          <span className={`sticker text-[10px] ${overdue ? "pink" : "orange"}`}>
            {overdue ? "OVERDUE" : `BY ${new Date(deadline).toLocaleDateString()}`}
          </span>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-mono-b font-bold uppercase">COURIER</label>
          <select
            data-testid={`ship-courier-${order.order_id}`}
            value={courier}
            onChange={(e) => setCourier(e.target.value)}
            className="brutal-input w-full mt-1 uppercase"
          >
            {COURIERS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-mono-b font-bold uppercase">AWB / TRACKING #</label>
          <input
            data-testid={`ship-awb-${order.order_id}`}
            value={awb}
            onChange={(e) => setAwb(e.target.value)}
            className="brutal-input w-full mt-1"
            required
          />
        </div>
      </div>
      <div>
        <label className="text-xs font-mono-b font-bold uppercase block mb-1">PACKAGE PHOTO</label>
        <ImageUpload
          value={photo ? [photo] : []}
          onChange={(imgs) => setPhoto(imgs[0] || "")}
          max={1}
          testIdPrefix={`ship-photo-${order.order_id}`}
        />
      </div>
      <div>
        <label className="text-xs font-mono-b font-bold uppercase">NOTES (optional)</label>
        <input
          data-testid={`ship-notes-${order.order_id}`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="brutal-input w-full mt-1"
        />
      </div>
      <button
        type="submit"
        disabled={saving}
        data-testid={`ship-submit-${order.order_id}`}
        className="bg-black text-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-4 py-2 font-mono-b font-bold uppercase inline-flex items-center gap-2 disabled:opacity-50"
      >
        <Upload className="w-4 h-4" /> {saving ? "UPLOADING..." : "MARK AS SHIPPED"}
      </button>
    </form>
  );
}
